import * as THREE from 'three';
import { ScatterBatchPass, SCATTER_BATCHING } from './scatterBatch';

const cells = new Set<RenderCell>();
const local = new THREE.Sphere();

/** An immutable local placement group. Individual mesh bounds/LOD still do
 * their normal fine culling; this skips whole offscreen scatter tiles before
 * Three walks their many children. Bounds include invisible resident children,
 * both geometry LODs and their existing wind margins. */
export class RenderCell extends THREE.Group {
  readonly bounds = new THREE.Sphere();
  private dirty = true;

  constructor() {
    super(); this.name = 'scatter-render-cell'; this.matrixAutoUpdate = false;
    cells.add(this);
  }
  override add(...objects: THREE.Object3D[]): this { this.dirty = true; return super.add(...objects); }
  override remove(...objects: THREE.Object3D[]): this { this.dirty = true; return super.remove(...objects); }

  localBounds(): THREE.Sphere {
    if (!this.dirty) return this.bounds;
    this.bounds.makeEmpty();
    for (const object of this.children) {
      const mesh = object as THREE.Mesh | THREE.InstancedMesh;
      if (!mesh.isMesh || !mesh.frustumCulled) {
        this.bounds.set(new THREE.Vector3(), Infinity); break;
      }
      if ('boundingSphere' in mesh) {
        if (!mesh.boundingSphere) mesh.computeBoundingSphere();
        local.copy(mesh.boundingSphere!);
      } else {
        if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
        local.copy(mesh.geometry.boundingSphere!);
      }
      if (mesh.matrixAutoUpdate) mesh.updateMatrix();
      this.bounds.union(local.applyMatrix4(mesh.matrix));
    }
    this.dirty = false;
    return this.bounds;
  }
  dispose(): void { cells.delete(this); this.removeFromParent(); }
}

/** Cull against every view that this render will use. Offscreen cells must stay
 * resident when they intersect a light's shadow volume. */
export class RenderCellPass {
  private readonly hidden: RenderCell[] = [];
  private readonly frustum = new THREE.Frustum();
  private readonly projection = new THREE.Matrix4();
  private readonly world = new THREE.Sphere();
  private readonly shadowViews: THREE.Frustum[] = [];
  private readonly batches = new Map<number, ScatterBatchPass>();
  private batch: ScatterBatchPass | null = null;

  begin(scene: THREE.Scene, camera: THREE.Camera, shadows = false, pass = 0): void {
    this.projection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projection);
    let shadowCount = 0;
    if (shadows) scene.traverseVisible(object => {
      const light = object as THREE.Light & { shadow?: THREE.LightShadow };
      if (!light.isLight || !light.castShadow || !light.layers.test(camera.layers) || !light.shadow) return;
      const shadow = light.shadow;
      if (!shadow.autoUpdate && !shadow.needsUpdate) return;
      // Match Three's shadow camera preparation, including all six point-light
      // views. Clone each frustum: updateMatrices reuses the shadow's own one.
      (shadow.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera).updateProjectionMatrix();
      for (let face = 0; face < shadow.getViewportCount(); face++) {
        if ((shadow as THREE.PointLightShadow).isPointLightShadow) (shadow as THREE.PointLightShadow).updateMatrices(light as THREE.PointLight, face);
        else shadow.updateMatrices(light);
        const view = this.shadowViews[shadowCount] ??= new THREE.Frustum();
        view.copy(shadow.getFrustum()); shadowCount++;
      }
    });
    const intersects = (sphere: THREE.Sphere): boolean => {
      if (this.frustum.intersectsSphere(sphere)) return true;
      for (let i = 0; i < shadowCount; i++) if (this.shadowViews[i].intersectsSphere(sphere)) return true;
      return false;
    };
    if (SCATTER_BATCHING.enabled) {
      this.batch = this.batches.get(pass) ?? new ScatterBatchPass(); this.batches.set(pass, this.batch);
    }
    for (const cell of cells) {
      if (!cell.visible || !cell.frustumCulled) continue;
      let root: THREE.Object3D = cell, ancestorVisible = true;
      while (root.parent) { root = root.parent; if (!root.visible) ancestorVisible = false; }
      if (root !== scene || !ancestorVisible) continue;
      this.world.copy(cell.localBounds()).applyMatrix4(cell.matrixWorld);
      if (!intersects(this.world)) { this.hidden.push(cell); cell.visible = false; continue; }
      if (this.batch && cell.parent && cell.matrix.equals(identity)) for (const child of cell.children) {
        const mesh = child as THREE.InstancedMesh;
        if (!mesh.isInstancedMesh || !mesh.visible || !mesh.layers.test(camera.layers) || !mesh.frustumCulled) continue;
        if (!mesh.boundingSphere) mesh.computeBoundingSphere();
        this.world.copy(mesh.boundingSphere!).applyMatrix4(mesh.matrixWorld);
        if (intersects(this.world)) {
          const e = camera.matrixWorldInverse.elements, p = this.world.center;
          this.batch.add(mesh, cell.parent, -(e[2] * p.x + e[6] * p.y + e[10] * p.z + e[14]));
        }
      }
    }
    this.batch?.begin(scene);
  }
  end(): void { this.batch?.end(); this.batch = null; for (const cell of this.hidden) cell.visible = true; this.hidden.length = 0; }
  dispose(): void { this.end(); for (const batch of this.batches.values()) batch.dispose(); this.batches.clear(); }
  stats(): { batches: number; instanceBytes: number } {
    let batches = 0, instanceBytes = 0;
    for (const pass of this.batches.values()) { const s = pass.stats(); batches += s.batches; instanceBytes += s.instanceBytes; }
    return { batches, instanceBytes };
  }
  /** Runtime A/B avoids importing a second Vite module instance in dev tools. */
  setBatchingEnabled(enabled: boolean): void { SCATTER_BATCHING.enabled = enabled; }
}
const identity = new THREE.Matrix4();
